/**
 * TypeBox schemas for Activity entity — a recorded outing.
 */

import { Type, type Static } from 'typebox'
import { UUID, ISODate, type Exact } from './common.js'
import { OriginSchema } from './origin.js'

// --- Weather Snapshot (recorded conditions for completed activities) ---

const SkyConditionSchema = Type.Union([
  Type.Literal('clear'),
  Type.Literal('mostly-clear'),
  Type.Literal('partly-cloudy'),
  Type.Literal('overcast'),
  Type.Literal('fog'),
  Type.Literal('drizzle'),
  Type.Literal('rain'),
  Type.Literal('freezing-rain'),
  Type.Literal('snow'),
  Type.Literal('snow-showers'),
  Type.Literal('rain-showers'),
  Type.Literal('thunderstorm'),
])

const CurrentConditionsSchema = Type.Object({
  temperature: Type.Number(),
  apparentTemperature: Type.Number(),
  skyCondition: SkyConditionSchema,
  windSpeed: Type.Number(),
  windGusts: Type.Number(),
  windDirection: Type.Number(),
  humidity: Type.Number(),
  cloudCover: Type.Number(),
  isDay: Type.Boolean(),
  fetchedAt: Type.String(),
})

const HourlyForecastSchema = Type.Object({
  time: Type.String(),
  temperature: Type.Number(),
  apparentTemperature: Type.Number(),
  skyCondition: SkyConditionSchema,
  precipProbability: Type.Number(),
  precipAmount: Type.Number(),
  snowfall: Type.Number(),
  windSpeed: Type.Number(),
  windGusts: Type.Number(),
  windDirection: Type.Number(),
  humidity: Type.Number(),
  cloudCover: Type.Number(),
})

const WeatherSnapshotSchema = Type.Object({
  current: CurrentConditionsSchema,
  hourly: Type.Array(HourlyForecastSchema),
  source: Type.Literal('open-meteo'),
  snapshotAt: Type.String(),
})

export const ActivityTypeSchema = Type.Union([
  Type.Literal('hike'),
  Type.Literal('run'),
  Type.Literal('bike'),
  Type.Literal('ski'),
  Type.Literal('paddle'),
  Type.Literal('other'),
])
export type ActivityType = Static<typeof ActivityTypeSchema>

export const ActivitySchema = Type.Object({
  id: UUID,
  name: Type.Optional(Type.String({ minLength: 1, maxLength: 100 })),
  /** True when the user typed the name themselves — auto-naming (plan/geocode)
   *  must never overwrite it. */
  userNamed: Type.Optional(Type.Boolean()),
  description: Type.Optional(Type.String({ maxLength: 2000 })),
  activityType: ActivityTypeSchema,
  startTime: Type.Optional(ISODate),
  endTime: Type.Optional(ISODate),
  conditions: Type.Optional(Type.String({ maxLength: 2000 })),
  weatherSnapshot: Type.Optional(WeatherSnapshotSchema),
  origin: OriginSchema,
  createdBy: UUID,
  version: Type.Number({ minimum: 0 }),
  createdAt: ISODate,
  updatedAt: ISODate,
})
export type Activity = Exact<Static<typeof ActivitySchema>>

/** Fields required when creating an activity */
export const ActivityCreateSchema = Type.Object({
  name: Type.Optional(Type.String({ minLength: 1, maxLength: 100 })),
  activityType: ActivityTypeSchema,
  origin: OriginSchema,
  createdBy: UUID,
})

/** Partial activity for updates */
export const ActivityUpdateSchema = Type.Partial(ActivitySchema)
